import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { PermissionsService } from 'app/core/auth/permissions.service';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Observable, ReplaySubject, tap } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _permissionsService = inject(PermissionsService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);
    private _rawNavigation: Navigation | null = null;

    constructor() {
        // Re-filter navigation whenever permissions change (fixes race condition on refresh)
        this._permissionsService.permissions$.subscribe(() => {
            if (this._rawNavigation) {
                this._navigation.next(this._filterNavigation(this._rawNavigation));
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) => {
                this._rawNavigation = navigation;
            }),
            map((navigation) => this._filterNavigation(navigation)),
            tap((navigation) => {
                this._navigation.next(navigation);
            })
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _filterNavigation(navigation: Navigation): Navigation {
        const result = this._permissionsService.getAllowedNavItemIds();

        // null means super_admin or role not loaded — no filtering needed
        if (result === null) return navigation;

        const { allowed, controlled } = result;

        const filter = (items: FuseNavigationItem[]): FuseNavigationItem[] => {
            return items.reduce<FuseNavigationItem[]>((acc, item) => {
                if (item.type === 'divider') {
                    acc.push(item);
                    return acc;
                }

                if (item.type === 'basic') {
                    // Keep if: allowed by permission OR not controlled (free route like Dashboard)
                    if (allowed.includes(item.id) || !controlled.includes(item.id)) {
                        acc.push(item);
                    }
                    return acc;
                }

                if (item.type === 'collapsable' && item.children) {
                    const filteredChildren = filter(item.children);
                    // Only include the parent group if it has at least one visible child
                    if (filteredChildren.length > 0) {
                        acc.push({ ...item, children: filteredChildren });
                    }
                    return acc;
                }

                acc.push(item);
                return acc;
            }, []);
        };

        return {
            compact:     filter(navigation.compact),
            default:     filter(navigation.default),
            futuristic:  filter(navigation.futuristic),
            horizontal:  filter(navigation.horizontal),
        };
    }
}
